// Vendor imports
const classNames = require('classnames')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const { CircleButton } = require('client-v2/common/components/buttons')
const UserAvatar = require('client-v2/common/components/user-avatar')
const tag = require('client-v2/common/utils/hyperscript')
// Sibling imports
const useKeyboardSelection = require('./hooks/keyboard-selection')

const bem = BemEntity('SearchForm')

function SearchForm({
  className,
  suggestions,
  value,
  onChange,
  onReset,
  onSubmit
}) {
  const inputRef = React.useRef()
  // Focus on the form input immediately after rendering the first time
  React.useEffect(
    () => {
      inputRef.current.focus()
    },
    [ inputRef ]
  )
  const [ activeIndex, setActiveIndex ] = React.useState(null)
  const [ isSuggestionsVisible, setSuggestionsVisible ] = React.useState(false)
  const showSuggestions = React.useCallback(
    () => {
      setActiveIndex(null)
      setSuggestionsVisible(true)
    },
    [ setActiveIndex, setSuggestionsVisible ]
  )
  const hideSuggestions = React.useCallback(
    () => {
      setActiveIndex(null)
      setSuggestionsVisible(false)
    },
    [ setSuggestionsVisible ]
  )
  const handleInputChange = React.useCallback(
    event => {
      showSuggestions()
      onChange(event.target.value)
    },
    [ onChange, showSuggestions ]
  )
  const handleReset = React.useCallback(
    event => {
      event.preventDefault()
      hideSuggestions()
      onReset()
    },
    [ onReset, hideSuggestions ]
  )
  const handleSubmit = React.useCallback(
    event => {
      event.preventDefault()
      hideSuggestions()
      onSubmit()
    },
    [ onSubmit, hideSuggestions ]
  )
  const onSuggestionIndexSelect = React.useCallback(
    index => {
      onSubmit(suggestions[index])
    },
    [ suggestions, onSubmit ]
  )
  /**
   * The input blur event pre-empts the suggestion click event;
   * delay hiding suggestions so that the click properly registers.
   */
  const hideSuggestionsTimerRef = React.useRef()
  const hideSuggestionsAfterDelay = React.useCallback(
    () => {
      hideSuggestionsTimerRef.current = setTimeout(() => {
        hideSuggestions()
      }, 250)
    },
    [ hideSuggestions ]
  )
  /*
   * Cancel the timer after the form unmounts to prevent Warning:
   * "Can't perform a React state update on an unmounted component."
   */
  React.useEffect(
    () => {
      return () => {
        clearTimeout(hideSuggestionsTimerRef.current)
      }
    },
    [ hideSuggestionsTimerRef ]
  )
  // Accessibility
  const aria = {
    autocomplete: 'list',
    expanded: isSuggestionsVisible,
    owns: 'SearchForm__suggestions-list'
  }
  if (activeIndex !== null) {
    aria.activedescendant = `SearchForm__suggestion:${activeIndex}`
  }
  // Render
  return (
    tag('form', {
      className: classNames(
        className,
        bem.block()
      ),
      onReset: handleReset,
      onSubmit: handleSubmit
    },
      tag(CircleButton, {
        className: bem.element('button', 'submit'),
        type: 'submit'
      }),
      tag('input', {
        ref: inputRef,
        aria,
        className: bem.element('form-input'),
        placeholder: 'Find people, collections and pages',
        role: 'combobox',
        value: value,
        onBlur: hideSuggestionsAfterDelay,
        onChange: handleInputChange,
        onFocus: showSuggestions
      }),
      tag(CircleButton, {
        className: bem.element('button', 'reset'),
        type: 'reset'
      }),
      isSuggestionsVisible && (
        tag(SearchFormSuggestions, {
          activeIndex,
          suggestions,
          onDismiss: hideSuggestions,
          onIndexFocus: setActiveIndex,
          onIndexSelect: onSuggestionIndexSelect
        })
      )
    )
  )
}

/**
 * @param {Object} props
 * @param {number} props.activeIndex
 * @param {any[]} props.suggestions
 * @param {() => void} props.onDismiss
 * @param {(index: number) => void} props.onIndexFocus
 * @param {(index: number) => void} props.onIndexSelect
 * @returns {React.ElementType}
 */
function SearchFormSuggestions({
  activeIndex,
  suggestions,
  onDismiss,
  onIndexFocus,
  onIndexSelect
}) {
  const rowCount = suggestions.length
  // Hooks
  const scrollableRef = React.useRef()
  useKeyboardSelection({
    activeIndex,
    rowCount,
    scrollableRef,
    onDismiss,
    onIndexFocus,
    onIndexSelect
  })
  const handleItemHover = React.useCallback(
    event => {
      const { index } = event.currentTarget.dataset
      onIndexFocus(+index)
    },
    [ onIndexFocus ]
  )
  const handleItemSelect = React.useCallback(
    event => {
      const { index } = event.currentTarget.dataset
      onIndexSelect(+index)
    },
    [ onIndexSelect ]
  )
  useClampActiveIndex({
    activeIndex,
    rowCount,
    setActiveIndex: onIndexFocus
  })
  // Render
  if (!rowCount) {
    return null
  }
  return (
    tag('div', {
      ref: scrollableRef,
      className: bem.element('suggestions')
    },
      tag('ul', {
        className: bem.element('suggestions-list'),
        id: 'SearchForm__suggestions-list',
        role: 'listbox'
      },
        suggestions.map((suggestion, index) => (
          tag('li', {
            id: `SearchForm__suggestion:${index}`,
            key: index,
            dataset: { index },
            role: 'option',
            onClick: handleItemSelect,
            onMouseEnter: handleItemHover
          },
            tag(SearchFormSuggestion, {
              ...suggestion,
              isActive: index === activeIndex
            })
          )
        ))
      )
    )
  )
}

/**
 * @param {Object} options
 * @param {number|null} options.activeIndex
 * @param {number} options.rowCount
 * @param {(activeIndex: number|null) => void} options.setActiveIndex
 * @returns {void}
 */
function useClampActiveIndex({
  activeIndex,
  rowCount,
  setActiveIndex
}) {
  React.useEffect(
    () => {
      let clampedIndex = null
      if (activeIndex !== null && rowCount) {
        // Clamp activeIndex within items
        clampedIndex = Math.max(0, Math.min(rowCount - 1, activeIndex))
      }
      if (clampedIndex !== activeIndex) {
        setActiveIndex(clampedIndex)
      }
    },
    [ activeIndex, rowCount, setActiveIndex ]
  )
}

/**
 * @param {Object} props
 * @param {string} props.complete
 * @param {boolean} props.isActive
 * @param {[string, string]} props.names
 * @param {string} props.photo
 * @param {string} props.type
 * @returns {React.ElementType}
 */
function SearchFormSuggestion({
  complete,
  isActive,
  names,
  photo,
  type
}) {
  if (type === 'contact') {
    return (
      tag('div', {
        className: bem.element('suggestion', {
          active: isActive
        })
      },
        tag(UserAvatar, {
          className: bem.element('suggestion-photo'),
          user: {
            'photo_url': photo,
            username: complete
          }
        }),
        tag('span', {
          className: bem.element('suggestion-name')
        },
          names[0]
        ),
        tag('span', {
          className: bem.element('suggestion-username')
        },
          names[1]
        )
      )
    )
  }
  return (
    tag('div', {
      className: bem.element('suggestion', {
        active: isActive
      })
    },
      names[0]
    )
  )
}

if (__DEV__) {
  const PropTypes = require('prop-types')

  SearchForm.propTypes = {
    // Values
    className: PropTypes.string,
    suggestions: PropTypes.arrayOf(PropTypes.object),
    value: PropTypes.string,
    // Callbacks
    onChange: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired
  }
}

module.exports = SearchForm
